import React from 'react';
import * as Styled from './Overstep.styles';
import useOverstepPage from './useOverstepPage';
import { useOverstepTracking } from './useOverstepTracking';
import { PATHS } from '@ids/router/routes';
import { useIdsGaTracking, useIDSPush } from '@ids/hooks';
import { useOverstepPageContext } from '@ids/context/OverstepPage';
import { useAppshellMeta, useAppShellPush, useAppshellRouter, useFeatureToggle } from '@ids/appshell';

type OverstepProps = React.PropsWithChildren<{
    'data-testid'?: string;
    className?: string;
}>;

function Overstep(props: OverstepProps) {
    const idsPush = useIDSPush();
    const { isApp } = useAppshellMeta();
    const { routes } = useAppshellRouter();
    const appshellPush = useAppShellPush();
    const { shopBellaCart, p100StudioPriceTiers } = useFeatureToggle();
    const { data } = useOverstepPageContext();
    const { trackIdsEvent } = useIdsGaTracking();
    const { trackPageViewEvent, resetCheck } = useOverstepTracking();
    const { disableRedirectsToOverstep, enableStartOver } = useOverstepPage();

    React.useEffect(() => {
        trackPageViewEvent();
    }, [trackPageViewEvent]);

    if (!data) {
        return null;
    }

    const handleCartButtonClick = () => {
        trackIdsEvent({
            action: 'Overstep page click',
            label: 'Redirect to cart - click',
        });

        let path = routes.ALICE_CART;
        if (shopBellaCart) {
            path = routes.CART;
            if (isApp) {
                path = routes.CART_NATIVE;
            }
        }

        appshellPush(path);
    };

    const handleStartoverClick = async () => {
        await disableRedirectsToOverstep();
        if (isApp && p100StudioPriceTiers) {
            enableStartOver();
        }
        trackIdsEvent({
            action: 'Overstep page click',
            label: 'Redirect to IDS - click',
        });

        return p100StudioPriceTiers ? idsPush.pushToSection('PRICE_TIERS') : idsPush(PATHS.QUESTIONNAIRE);
    };

    const handleCTAClick = () => {
        resetCheck();
    };

    return (
        <Styled.OverstepContainer className={props?.className} data-testid={props?.['data-testid']}>
            <Styled.BackgroundImage image={data.backgroundImage} />
            <Styled.ContentContainer>
                <Styled.Heading>{data.title}</Styled.Heading>
                <Styled.ContentText>{data.contentText}</Styled.ContentText>
                <Styled.CTAContainer onClick={handleCTAClick}>
                    <Styled.CartButton onClick={handleCartButtonClick}>{data.cartCtaText}</Styled.CartButton>
                    <Styled.StartoverButton onClick={handleStartoverClick}>
                        {data.startOverCtaText}
                    </Styled.StartoverButton>
                </Styled.CTAContainer>
            </Styled.ContentContainer>
        </Styled.OverstepContainer>
    );
}

export default Overstep;
